import { configureChildApp } from '@rendora/common/utils/appContext';
import BottomDrawer from '@rendora/design/components/bottom-drawer/BottomDrawer.vue';

import { createApp, nextTick, provide, ref, type Component, type VNode } from 'vue';

// BottomDrawer 配置接口
export interface BottomDrawerConfig {
  content?: Component | VNode;
  /** 是否可拖拽关闭 */
  draggable?: boolean;
  /** 拖拽关闭阈值（像素） */
  closeThreshold?: number;
  /** 点击遮罩是否关闭 */
  closeOnClickMask?: boolean;
  /** 是否锁定页面滚动 */
  lockScroll?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

// BottomDrawer 实例接口
export interface BottomDrawerInstance {
  close: () => void;
  update: (config: Partial<BottomDrawerConfig>) => void;
}

// BottomDrawer 工厂类
export class BottomDrawerFactory {
  private static instances = new Map<symbol, BottomDrawerInstance>();
  private static drawerContainer: HTMLElement | null = null;
  private static zIndexCounter = 900;
  private static readonly MAX_Z_INDEX = 1000;

  static getInstance(symbol: symbol): BottomDrawerInstance | undefined {
    return BottomDrawerFactory.instances.get(symbol);
  }

  // 类型守卫
  private static isValidDrawerInstance(
    instance: BottomDrawerInstance | undefined,
  ): instance is BottomDrawerInstance {
    return instance !== undefined && typeof instance.close === 'function';
  }

  // 创建 BottomDrawer 容器
  private static createContainer(): HTMLElement {
    if (!this.drawerContainer) {
      this.drawerContainer = document.createElement('div');
      this.drawerContainer.id = 'bottom-drawer-container';
      this.drawerContainer.style.position = 'fixed';
      this.drawerContainer.style.top = '0';
      this.drawerContainer.style.left = '0';
      this.drawerContainer.style.width = '100%';
      this.drawerContainer.style.height = '100%';
      this.drawerContainer.style.pointerEvents = 'none';
      document.body.appendChild(this.drawerContainer);
    }
    return this.drawerContainer;
  }

  // 创建基础 BottomDrawer
  static create(config: BottomDrawerConfig, symbol?: symbol): BottomDrawerInstance {
    const drawerSymbol = symbol || Symbol('bottom-drawer');
    const visible = ref(false);

    // 防止 z-index 溢出
    if (this.zIndexCounter > this.MAX_Z_INDEX) {
      this.zIndexCounter = 900;
    }

    const currentConfig = ref<BottomDrawerConfig>({
      draggable: true,
      closeThreshold: 100,
      closeOnClickMask: true,
      lockScroll: true,
      ...config,
    });

    // 创建容器
    const container = this.createContainer();

    // 检查是否已存在相同的 drawer
    if (this.instances.has(drawerSymbol)) {
      // eslint-disable-next-line no-console
      console.warn('BottomDrawer with this symbol already exists, replacing it');
      const existingInstance = this.instances.get(drawerSymbol);
      if (this.isValidDrawerInstance(existingInstance)) {
        existingInstance.close();
      }
    }

    // 创建单个 drawer 的包装器
    const drawerWrapper = document.createElement('div');
    drawerWrapper.style.position = 'absolute';
    drawerWrapper.style.top = '0';
    drawerWrapper.style.left = '0';
    drawerWrapper.style.width = '100%';
    drawerWrapper.style.height = '100%';
    drawerWrapper.style.pointerEvents = 'auto';
    drawerWrapper.style.zIndex = String(++this.zIndexCounter);

    const drawerInstance: BottomDrawerInstance = {
      close: () => {
        visible.value = false;
        setTimeout(() => {
          try {
            // 确保应用正确卸载
            if (app) {
              app.unmount();
            }
            // 确保 DOM 节点被移除
            if (drawerWrapper && drawerWrapper.parentNode) {
              drawerWrapper.parentNode.removeChild(drawerWrapper);
            }
            // 从实例映射中删除
            BottomDrawerFactory.instances.delete(drawerSymbol);
            // 调用关闭回调
            currentConfig.value.onClose?.();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to close bottom drawer:', error);
            // 即使出错也要清理实例
            BottomDrawerFactory.instances.delete(drawerSymbol);
          }
        }, 300);
      },
      update: (newConfig: Partial<BottomDrawerConfig>) => {
        try {
          Object.assign(currentConfig.value, newConfig);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update bottom drawer config:', error);
        }
      },
    };

    // 创建 Vue 应用
    const app = createApp({
      setup() {
        const handleClose = () => {
          drawerInstance.close();
        };

        const handleOpen = () => {
          currentConfig.value.onOpen?.();
        };

        provide('drawerInstance', drawerInstance);

        return () => (
          <BottomDrawer
            modelValue={visible.value}
            {...{
              'onUpdate:modelValue': (val: boolean) => {
                visible.value = val;
                if (!val) {
                  handleClose();
                }
              },
            }}
            draggable={currentConfig.value.draggable}
            closeThreshold={currentConfig.value.closeThreshold}
            closeOnClickMask={currentConfig.value.closeOnClickMask}
            lockScroll={currentConfig.value.lockScroll}
            onClose={handleClose}
            onOpen={handleOpen}
          >
            {currentConfig.value.content}
          </BottomDrawer>
        );
      },
    });

    BottomDrawerFactory.instances.set(drawerSymbol, drawerInstance);

    // 配置抽屉应用 - 继承主应用的 i18n、指令等
    configureChildApp(app);

    // 挂载应用
    container.appendChild(drawerWrapper);
    app.mount(drawerWrapper);

    // 显示动画
    nextTick(() => {
      visible.value = true;
    });

    return drawerInstance;
  }

  // 清除所有 drawer
  static clear() {
    try {
      this.instances.forEach((instance, symbol) => {
        try {
          if (this.isValidDrawerInstance(instance)) {
            instance.close();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to close bottom drawer instance:', symbol.toString(), error);
        }
      });
      this.instances.clear();

      if (this.drawerContainer && this.drawerContainer.parentNode) {
        this.drawerContainer.parentNode.removeChild(this.drawerContainer);
        this.drawerContainer = null;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to clear bottom drawers:', error);
      // 即使出错也要重置状态
      this.instances.clear();
      this.drawerContainer = null;
    }
  }

  // 获取当前活跃的 drawer 数量
  static getActiveDrawerCount(): number {
    return this.instances.size;
  }

  // 检查是否存在指定的 drawer
  static hasDrawer(symbol: symbol): boolean {
    return this.instances.has(symbol);
  }
}
